import React, {createContext, useContext, useState, useEffect, useMemo, useCallback} from 'react';
import {loginApi, registerApi, onboardingImportCollectiveApi, getProviderAuthUrlFromApi, verifyCodeApi, getOAuthCallbackPath, getApiBaseUrl, getCreatorApi, fetchKbStatus} from '../../utils/api';
import {getAccountProfile} from '../../utils/accountApi';
import {DEFAULT_CURRENCY_CODE, formatCurrency, getCurrency, getCurrencySymbol, resolveUserCurrency} from '../../utils/currency';

const AUTH_USER_KEY = 'koenig-auth-user';
const AUTH_TOKENS_KEY = 'koenig-auth-tokens';

// All app-owned localStorage keys cleared on logout. Cookie-consent is intentionally
// excluded so the user does not re-see the consent banner after signing out.
const APP_LS_KEYS = [
    AUTH_USER_KEY,
    AUTH_TOKENS_KEY,
    'kbCreatorUsername',
    'kbHintChips',
    'onboardingRole',
    'onboardingProfile',
    'onboardingCompleted',
    'profileSlug',
    'username',
    'preSelectedShopifyTemplate',
    'connectedShopDomain',
    'shopifyTemplateLeadId',
    'brandOnbDisplayName',
    'brandOnbTemplateId',
    'designer-last-temp-profile-id',
    'designer-last-temp-slug',
    'DESIGNER_LAST_TEMP_PROFILE_ID',
    'DESIGNER_LAST_TEMP_SLUG',
    'koenig-profile-data',
    'koenig-articles',
    'koenig-knowledge-sources',
    'koenig-admin-sidebar-width',
    'koenig-sidebar-width',
    'koenig-sub-sidebar-width',
    'sidebar-state',
    'oi_dash_theme',
    'oi_globe_theme',
    'kb_chat_tracking',
    'checkout_session_id',
    'optimus_checkout_session_id',
    'calendly:connect:result',
    'ytChannelLookupCache_v1',
    'igProfileLookupCache_v1',
    'checkoutLinks.viewMode',
    'ig_post_frames_v1',
    'ig_post_media_v1',
    'ig_stories_v1',
];

const APP_LS_PREFIXES = ['koenig-shared-', 'mykiosk_cart_'];

const clearAppLocalStorage = () => {
    try {
        APP_LS_KEYS.forEach(k => { try { localStorage.removeItem(k); } catch { /* ignore */ } });
        Object.keys(localStorage).forEach(k => {
            if (APP_LS_PREFIXES.some(p => k.startsWith(p))) {
                try { localStorage.removeItem(k); } catch { /* ignore */ }
            }
        });
    } catch { /* private mode / quota */ }
};

const AuthContext = createContext(null);

/** Message type for OAuth popup → opener (any provider). */
const OAUTH_MESSAGE_TYPE = 'OAUTH_AUTH_RESULT';

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [tokens, setTokens] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const syncFromStorage = () => {
            const savedUser = localStorage.getItem(AUTH_USER_KEY);
            const savedTokens = localStorage.getItem(AUTH_TOKENS_KEY);
            if (savedUser && savedTokens) {
                try {
                    setUser(JSON.parse(savedUser));
                    setTokens(JSON.parse(savedTokens));
                    return;
                } catch {
                    localStorage.removeItem(AUTH_USER_KEY);
                    localStorage.removeItem(AUTH_TOKENS_KEY);
                }
            }
            // If nothing valid in storage, ensure state is cleared
            setUser(null);
            setTokens(null);
        };

        syncFromStorage();
        setIsLoading(false);

        const onStorage = (event) => {
            if (event.key === AUTH_USER_KEY || event.key === AUTH_TOKENS_KEY || event.key === null) {
                // Any relevant change in auth-related keys → resync
                syncFromStorage();
            }
        };
        window.addEventListener('storage', onStorage);
        return () => window.removeEventListener('storage', onStorage);
    }, []);

    // If auth state becomes valid (e.g. after OAuth popup writes to localStorage),
    // and user is on login / auth callback page, redirect to dashboard + fire success toast.
    // NOTE: Redirect on OAuth success is handled via postMessage in loginWithProvider.

    const persistAuth = (userData, tokenData) => {
        setUser(userData);
        setTokens(tokenData);
        localStorage.setItem(AUTH_USER_KEY, JSON.stringify(userData));
        localStorage.setItem(AUTH_TOKENS_KEY, JSON.stringify(tokenData));
        try { localStorage.removeItem('kbCreatorUsername'); } catch {}
    };

    // Ensure kbCreatorUsername is in localStorage after login/signup
    useEffect(() => {
        if (!tokens?.accessToken || !user?.kbConnected) return;

        fetchKbStatus(tokens.accessToken).then(res => {
            if (res?.ok && res.data?.creator?.username) {
                try { localStorage.setItem('kbCreatorUsername', res.data.creator.username); } catch {}
            }
        }).catch(() => {});
    }, [tokens?.accessToken, user?.kbConnected]);

    // If the cached user object is missing `currency` (e.g. the user logged
    // in before the currency feature shipped), pull a fresh profile once so
    // every currency-aware component starts rendering the correct symbol
    // without waiting for the user to touch Settings.
    useEffect(() => {
        if (!tokens?.accessToken || !user) return;
        if (user.currency) return;
        getAccountProfile({ accessToken: tokens.accessToken })
            .then((res) => {
                if (res.ok && res.data?.user) {
                    persistAuth(res.data.user, tokens);
                }
            })
            .catch(() => {});
        // Intentionally only depends on token + presence of `currency` — we
        // don't want to re-fire on every user field change.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tokens?.accessToken, user?.currency]);

    /**
     * Login with backend API.
     * @param {{ email: string, password: string }} credentials
     * @returns {Promise<{ ok: boolean, error?: string }>}
     */
    const login = async (credentials) => {
        const result = await loginApi(credentials);
        if (result.ok && result.data) {
            persistAuth(result.data.user, result.data.tokens);
            return { ok: true };
        }
        return { ok: false, error: result.error || 'Login failed' };
    };

    /**
     * Register with backend API and log in.
     * @param {{ name: string, email: string, password: string }} credentials
     * @returns {Promise<{ ok: boolean, error?: string }>}
     */
    const signup = async (credentials) => {
        const result = await registerApi(credentials);
        if (result.ok && result.data) {
            persistAuth(result.data.user, result.data.tokens);
            onboardingImportCollectiveApi(result.data.tokens.accessToken).catch(() => { });
            return { ok: true };
        }
        return { ok: false, error: result.error || 'Registration failed' };
    };

    /**
     * Start OAuth for a provider in a popup.
     * Flow: API se URL get → child window me open → login ke baad code callback page pe aata hai → postMessage se parent ko code → verify-code API → login.
     * @param {string} providerId - e.g. 'google'
     * @param {{ from?: string }} options - e.g. { from: 'settings' } when linking from Account Settings.
     */
    const loginWithProvider = async (providerId, options = {}) => {
        const base = getOAuthCallbackPath();
        const redirectUrl = `${base}?provider=${encodeURIComponent(providerId)}${options.from ? '&from=settings' : ''}`;
        const result = await getProviderAuthUrlFromApi(providerId, redirectUrl);
        if (!result.ok || !result.url) {
            if (typeof window.__OAUTH_TOAST_ERROR === 'function') {
                window.__OAUTH_TOAST_ERROR(result.error || 'Could not start sign-in');
            }
            if (typeof window.__OAUTH_LOADING_DONE === 'function') window.__OAUTH_LOADING_DONE();
            return;
        }
        const url = result.url;
        const width = 500;
        const height = 620;
        const screenW = window.screen.availWidth || window.screen.width || 1024;
        const screenH = window.screen.availHeight || window.screen.height || 768;
        const left = Math.max(0, Math.round((screenW - width) / 2 + (window.screen.availLeft || window.screen.left || 0)));
        const top = Math.max(0, Math.round((screenH - height) / 2 + (window.screen.availTop || window.screen.top || 0)));
        const popup = window.open(
            url,
            `oauth_${providerId}`,
            `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no,scrollbars=yes,resizable=yes`
        );
        if (!popup) {
            window.location.href = url;
            if (typeof window.__OAUTH_LOADING_DONE === 'function') window.__OAUTH_LOADING_DONE();
            return;
        }
        const origin = window.location.origin;
        const apiOrigin = (() => {
            try {
                return new URL(getApiBaseUrl()).origin;
            } catch {
                return '';
            }
        })();
        const isCrossOrigin = apiOrigin && apiOrigin !== origin;
        const allowedMessageOrigins = [origin];
        if (apiOrigin) allowedMessageOrigins.push(apiOrigin);
        const timeout = window.setTimeout(() => cleanup(), 5 * 60 * 1000);
        // let closeCheck = null;
        // if (!isCrossOrigin) {
        //     closeCheck = setInterval(() => {
        //         if (popup.closed) {
        //             cleanup();
        //             if (typeof window.__OAUTH_LOADING_DONE === 'function') window.__OAUTH_LOADING_DONE();
        //         }
        //     }, 300);
        // }
        let closeCheck = setInterval(() => {
            if (!popup || popup.closed) {
                cleanup();
                if (typeof window.__OAUTH_LOADING_DONE === 'function') {
                    window.__OAUTH_LOADING_DONE();
                }
            }
        }, 300);

        function done() {
            if (typeof window.__OAUTH_LOADING_DONE === 'function') window.__OAUTH_LOADING_DONE();
        }

        function cleanup() {
            window.removeEventListener('message', onMessage);
            if (timeout) window.clearTimeout(timeout);
            if (closeCheck) clearInterval(closeCheck);
        }

        async function onMessage(event) {
            if (!allowedMessageOrigins.includes(event.origin) || event.data?.type !== OAUTH_MESSAGE_TYPE) return;
            cleanup();
            done();
            if (event.data.errorMessage) {
                if (event.data.fromSettings && typeof window.__OAUTH_ON_LINK_ERROR === 'function') {
                    window.__OAUTH_ON_LINK_ERROR(event.data.errorMessage);
                } else if (typeof window.__OAUTH_TOAST_ERROR === 'function') {
                    window.__OAUTH_TOAST_ERROR(event.data.errorMessage);
                }
                return;
            }
            if (event.data.code && event.data.provider) {
                const verifyResult = await verifyCodeApi({ code: event.data.code, provider: event.data.provider });
                if (verifyResult.ok && verifyResult.data) {
                    persistAuth(verifyResult.data.user, verifyResult.data.tokens);
                    if (event.data.fromSettings && typeof window.__OAUTH_ON_LINK_SUCCESS === 'function') {
                        window.__OAUTH_ON_LINK_SUCCESS();
                    } else {
                        if (typeof window.__OAUTH_TOAST_SUCCESS === 'function') {
                            window.__OAUTH_TOAST_SUCCESS();
                        }
                        const query = window.location.search || '';
                        window.location.href = `${window.location.origin}/onboarding${query}`;
                    }
                } else if (typeof window.__OAUTH_TOAST_ERROR === 'function') {
                    window.__OAUTH_TOAST_ERROR(verifyResult.error || 'Sign-in failed. Please try again.');
                }
            }
        }
        window.addEventListener('message', onMessage);
    };

    /**
     * Called by /auth/callback page with token data from Google OAuth redirect. Persists and returns ok.
     * @param {{ accessToken: string, expiresIn: number, user: object }} data
     * @returns {{ ok: boolean }}
     */
    const persistAuthFromCallback = (data) => {
        if (data?.accessToken && data?.user) {
            persistAuth(data.user, {
                accessToken: data.accessToken,
                expiresIn: Number(data.expiresIn) || 0,
            });
            return { ok: true };
        }
        return { ok: false };
    };

    const logout = () => {
        setUser(null);
        setTokens(null);
        clearAppLocalStorage();
    };

    /**
     * Refresh user profile from API (e.g. after account settings update).
     * @returns {Promise<{ ok: boolean, error?: string }>}
     */
    const refreshUser = async () => {
        if (!tokens?.accessToken) return { ok: false, error: 'Not authenticated' };
        const result = await getAccountProfile({ accessToken: tokens.accessToken });
        if (result.ok && result.data?.user) {
            persistAuth(result.data.user, tokens);
            return { ok: true };
        }
        return { ok: false, error: result.error || 'Failed to refresh profile' };
    };

    // Active currency exposed globally so any component (product cards,
    // cart, checkout, orders, earnings) can format prices without having
    // to know which user is signed in. Falls back to USD when the user is
    // logged out or the backend hasn't returned a currency yet.
    const currencyCode = useMemo(() => resolveUserCurrency(user), [user]);
    const currencyMeta = useMemo(() => getCurrency(currencyCode), [currencyCode]);
    const currencySymbol = currencyMeta.prefix;
    const formatPrice = useCallback(
        (amount, opts) => formatCurrency(amount, currencyCode, opts),
        [currencyCode],
    );

    const value = {
        user,
        tokens,
        isLoading,
        isAuthenticated: !!user,
        login,
        signup,
        loginWithProvider,
        /** @deprecated Use loginWithProvider('google') */
        loginWithGoogle: () => loginWithProvider('google'),
        persistAuthFromCallback,
        logout,
        refreshUser,
        // Currency surface — see `useCurrency()` for the ergonomic wrapper.
        currency: {
            code: currencyCode,
            symbol: currencySymbol,
            name: currencyMeta.name,
            format: formatPrice,
        },
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}

/**
 * `useCurrency()` — ergonomic hook returning the currently active currency.
 *
 * Returns `{ code, symbol, name, format }`. `format(amount, opts)` is a
 * ready-to-use wrapper around `formatCurrency` bound to the signed-in
 * user's picked currency, so components can just do:
 *
 *     const { symbol, format } = useCurrency();
 *     return <span>{format(product.price)}</span>;
 *
 * When AuthProvider is not mounted (e.g. rendering a fully public preview
 * outside the app shell), we return the USD defaults instead of throwing,
 * because pricing rendering shouldn't crash the page.
 */
export function useCurrency() {
    const context = useContext(AuthContext);
    if (context?.currency) return context.currency;
    const fallback = getCurrency(DEFAULT_CURRENCY_CODE);
    return {
        code: fallback.code,
        symbol: fallback.prefix,
        name: fallback.name,
        format: (amount, opts) => formatCurrency(amount, fallback.code, opts),
    };
}

export default AuthContext;
